import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Filter, Plus, Mail, Phone, Calendar, Edit, Trash2, UserPlus,
  RefreshCw, Building, Eye, Key, X, Save, Upload, CheckCircle, AlertCircle,
  Camera, Image
} from 'lucide-react';
import { userService } from '../../services/userService';

const Users = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  const [stats, setStats] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companies, setCompanies] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUploadLogoModal, setShowUploadLogoModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });

  // Estados para formularios
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    nombre_empresa: '',
    posicion: '',
    rol: 'usuario',
    password: '',
    confirmPassword: '',
    activo: 1
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Estado para la subida de logo
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Cargar usuarios
  const loadUsers = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const adminCheck = await userService.checkAdminStatus();

      if (!adminCheck.success) {
        setError('No se pudo verificar permisos: ' + adminCheck.error);
        setLoading(false);
        return;
      }

      if (!adminCheck.isAdmin) {
        setError('No tienes permisos de administrador para ver esta página');
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setIsAdmin(true);

      const filters = {
        search: searchTerm,
        rol: selectedRole || undefined,
        page: page,
        limit: pagination.limit
      };

      const result = await userService.getAllUsers(filters);

      if (result.success) {
        const formattedUsers = result.rawUsers ? result.rawUsers.map(user => ({
          id: user.id,
          name: `${user.nombre} ${user.apellido}`,
          email: user.email,
          phone: user.telefono || 'Sin teléfono',
          role: userService.mapRole(user.rol),
          status: user.activo ? 'active' : 'inactive',
          joinDate: userService.formatDate(user.fecha_creacion),
          lastLogin: userService.formatDate(user.fecha_actualizacion),
          company: user.nombre_empresa || 'Sin empresa',
          position: user.posicion || 'Sin posición',
          logo: user.logo_empresa,
          activo: user.activo,
          rawData: user
        })) : [];

        setUsers(formattedUsers);

        if (result.rawUsers) {
          const uniqueCompanies = [...new Set(result.rawUsers
            .map(user => user.nombre_empresa)
            .filter(Boolean))].sort();
          setCompanies(uniqueCompanies);
        }

        if (result.pagination) {
          setPagination(result.pagination);
        }
        if (result.estadisticas) {
          setStats(result.estadisticas);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error al cargar usuarios: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cargar usuarios al montar el componente
  useEffect(() => {
    loadUsers();
  }, []);

  // Filtrar usuarios localmente
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.position?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCompany = selectedCompany === '' || user.company === selectedCompany;

    return matchesSearch && matchesCompany;
  });

  // Funciones para manejar la subida de logo
  const handleLogoClick = (user) => {
    setSelectedUser(user);
    setSelectedFile(null);
    setPreviewUrl(null);
    setActionMessage({ type: '', text: '' });
    setShowUploadLogoModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) {
      setActionMessage({ type: 'error', text: 'Por favor selecciona una imagen' });
      return;
    }

    setActionLoading(true);
    setActionMessage({ type: '', text: '' });

    try {
      const result = await userService.uploadLogoForUser(selectedUser.id, selectedFile);

      if (result.success) {
        setActionMessage({ type: 'success', text: 'Foto de perfil actualizada correctamente' });

        // Actualizar la lista de usuarios después de 1.5 segundos
        setTimeout(() => {
          setShowUploadLogoModal(false);
          loadUsers();
        }, 1500);
      } else {
        setActionMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Error al subir la imagen' });
    } finally {
      setActionLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Resto de las funciones existentes permanecen igual...
  const handleCreateUser = () => {
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      nombre_empresa: '',
      posicion: '',
      rol: 'usuario',
      password: '',
      confirmPassword: '',
      activo: 1
    });
    setActionMessage({ type: '', text: '' });
    setShowCreateModal(true);
  };

  const handleEditUser = async (user) => {
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });

    try {
      const result = await userService.getUserById(user.id);

      if (result.success) {
        setSelectedUser(user);
        setFormData({
          nombre: result.user.nombre,
          apellido: result.user.apellido,
          email: result.user.email,
          telefono: result.user.telefono || '',
          nombre_empresa: result.user.nombre_empresa || '',
          posicion: result.user.posicion || '',
          rol: result.user.rol,
          password: '', // No cargar contraseña por seguridad
          confirmPassword: '',
          activo: result.user.activo
        });
        setShowEditModal(true);
      } else {
        setActionMessage({ type: 'error', text: result.error });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Error al cargar datos del usuario' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });

    try {
      const result = await userService.toggleUserStatus(selectedUser.id, false);

      if (result.success) {
        setActionMessage({ type: 'success', text: 'Usuario desactivado correctamente' });
        setTimeout(() => {
          setShowDeleteModal(false);
          loadUsers();
        }, 1500);
      } else {
        setActionMessage({ type: 'error', text: result.error });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Error al desactivar usuario' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });

    // Validar contraseña
    if (formData.password !== formData.confirmPassword) {
      setActionMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      setActionLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setActionMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' });
      setActionLoading(false);
      return;
    }

    try {
      // Preparar datos para enviar
      const userData = {
        email: formData.email,
        password: formData.password,
        nombre: formData.nombre,
        apellido: formData.apellido,
        telefono: formData.telefono || null,
        nombre_empresa: formData.nombre_empresa || null,
        posicion: formData.posicion || null,
        rol: formData.rol,
        activo: formData.activo ? 1 : 0
      };

      const result = await userService.createUserAsAdmin(userData);

      if (result.success) {
        setActionMessage({ type: 'success', text: 'Usuario creado exitosamente' });
        setTimeout(() => {
          setShowCreateModal(false);
          loadUsers();
        }, 1500);
      } else {
        setActionMessage({ type: 'error', text: result.error });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Error al crear usuario' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });

    try {
      // Preparar datos para actualizar
      const updateData = {};

      // Solo incluir campos que han cambiado
      if (formData.nombre !== selectedUser.rawData.nombre) updateData.nombre = formData.nombre;
      if (formData.apellido !== selectedUser.rawData.apellido) updateData.apellido = formData.apellido;
      if (formData.email !== selectedUser.rawData.email) updateData.email = formData.email;
      if (formData.telefono !== selectedUser.rawData.telefono) updateData.telefono = formData.telefono || null;
      if (formData.nombre_empresa !== selectedUser.rawData.nombre_empresa) updateData.nombre_empresa = formData.nombre_empresa || null;
      if (formData.posicion !== selectedUser.rawData.posicion) updateData.posicion = formData.posicion || null;
      if (formData.rol !== selectedUser.rawData.rol) updateData.rol = formData.rol;
      if (formData.activo !== selectedUser.rawData.activo) updateData.activo = formData.activo ? 1 : 0;

      // Si hay nueva contraseña
      if (formData.password && formData.password.trim() !== '') {
        if (formData.password !== formData.confirmPassword) {
          setActionMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
          setActionLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setActionMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' });
          setActionLoading(false);
          return;
        }
        updateData.password = formData.password;
      }

      // Si no hay cambios
      if (Object.keys(updateData).length === 0) {
        setActionMessage({ type: 'info', text: 'No hay cambios para guardar' });
        setActionLoading(false);
        return;
      }

      const result = await userService.updateUserAsAdmin(selectedUser.id, updateData);

      if (result.success) {
        setActionMessage({ type: 'success', text: 'Usuario actualizado exitosamente' });
        setTimeout(() => {
          setShowEditModal(false);
          loadUsers();
        }, 1500);
      } else {
        setActionMessage({ type: 'error', text: result.error });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Error al actualizar usuario' });
    } finally {
      setActionLoading(false);
    }
  };


  // Agrega esta función después de handleEditSubmit
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Agrega también la función handlePageChange que te falta
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.pages) {
      loadUsers(page);
    }
  };

  // Agrega esta función después de handleEditSubmit y antes de las funciones de utilidad

  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Editar Usuario</h2>
              <p className="text-sm text-slate-600">{selectedUser?.name}</p>
            </div>
            <button
              onClick={() => setShowEditModal(false)}
              className="p-1 hover:bg-slate-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleEditSubmit}>
            {actionMessage.text && (
              <div className={`mb-4 p-3 rounded-lg ${actionMessage.type === 'success' ? 'bg-emerald-50 text-emerald-800' : actionMessage.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-blue-50 text-blue-800'}`}>
                {actionMessage.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <AlertCircle className="w-4 h-4 inline mr-2" />}
                {actionMessage.text}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Apellido *</label>
                <input
                  type="text"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Empresa</label>
                <input
                  type="text"
                  name="nombre_empresa"
                  value={formData.nombre_empresa}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cargo/Posición</label>
                <input
                  type="text"
                  name="posicion"
                  value={formData.posicion}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rol *</label>
                <select
                  name="rol"
                  value={formData.rol}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {userService.getAvailableRoles().map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleFormChange}
                    className="rounded text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-slate-700">Usuario Activo</span>
                </label>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4 mb-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Cambiar Contraseña</h3>
              <p className="text-sm text-slate-600 mb-3">Dejar en blanco si no desea cambiar la contraseña</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nueva Contraseña</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Confirmar Contraseña</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                disabled={actionLoading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Guardar Cambios</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // También asegúrate de tener estas otras funciones de renderizado:

  const renderCreateModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-900">Crear Nuevo Usuario</h2>
            <button
              onClick={() => setShowCreateModal(false)}
              className="p-1 hover:bg-slate-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleCreateSubmit}>
            {actionMessage.text && (
              <div className={`mb-4 p-3 rounded-lg ${actionMessage.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                {actionMessage.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <AlertCircle className="w-4 h-4 inline mr-2" />}
                {actionMessage.text}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Apellido *</label>
                <input
                  type="text"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Empresa</label>
                <input
                  type="text"
                  name="nombre_empresa"
                  value={formData.nombre_empresa}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cargo/Posición</label>
                <input
                  type="text"
                  name="posicion"
                  value={formData.posicion}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rol *</label>
                <select
                  name="rol"
                  value={formData.rol}
                  onChange={handleFormChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {userService.getAvailableRoles().map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleFormChange}
                    className="rounded text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-slate-700">Usuario Activo</span>
                </label>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4 mb-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Contraseña</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contraseña *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Confirmar Contraseña *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">La contraseña debe tener al menos 6 caracteres</p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                disabled={actionLoading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creando...</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>Crear Usuario</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderDeleteModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-900">Desactivar Usuario</h2>
            <button
              onClick={() => setShowDeleteModal(false)}
              className="p-1 hover:bg-slate-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {actionMessage.text && (
            <div className={`mb-4 p-3 rounded-lg ${actionMessage.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
              {actionMessage.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <AlertCircle className="w-4 h-4 inline mr-2" />}
              {actionMessage.text}
            </div>
          )}

          <div className="mb-6">
            <p className="text-slate-700 mb-3">¿Estás seguro de que deseas desactivar al usuario?</p>
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="font-medium text-slate-900">{selectedUser?.name}</div>
              <div className="text-sm text-slate-600">{selectedUser?.email}</div>
              <div className="text-sm text-slate-600">{selectedUser?.company}</div>
            </div>
            <p className="text-sm text-slate-600 mt-3">El usuario no podrá iniciar sesión hasta que sea reactivado.</p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              disabled={actionLoading}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={actionLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {actionLoading ? 'Desactivando...' : 'Sí, desactivar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  // Funciones de utilidad
  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800';
  };

  const getRoleColor = (role) => {
    const colors = {
      'Administrador': 'bg-red-100 text-red-800',
      'Usuario Premium': 'bg-purple-100 text-purple-800',
      'Usuario Básico': 'bg-blue-100 text-blue-800',
      /* 'Editor': 'bg-orange-100 text-orange-800' */
    };
    return colors[role] || 'bg-slate-100 text-slate-800';
  };

  const truncateText = (text, maxLength = 25) => {
    if (!text) return 'Sin información';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Nuevo modal para subir logo
  const renderUploadLogoModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Actualizar Foto de Perfil</h2>
              <p className="text-sm text-slate-600">{selectedUser?.name}</p>
            </div>
            <button
              onClick={() => setShowUploadLogoModal(false)}
              className="p-1 hover:bg-slate-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {actionMessage.text && (
            <div className={`mb-4 p-3 rounded-lg ${actionMessage.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
              {actionMessage.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <AlertCircle className="w-4 h-4 inline mr-2" />}
              {actionMessage.text}
            </div>
          )}

          <div className="mb-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Vista previa"
                    className="w-32 h-32 rounded-full object-cover border-4 border-slate-200"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-slate-100 border-4 border-slate-200 flex flex-col items-center justify-center">
                    <Camera className="w-12 h-12 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Sin imagen</span>
                  </div>
                )}
                <button
                  onClick={triggerFileInput}
                  className="absolute bottom-0 right-0 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                className="hidden"
              />

              <div className="text-center">
                <button
                  onClick={triggerFileInput}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Seleccionar imagen
                </button>
                <p className="text-xs text-slate-500 mt-1">
                  Formatos permitidos: JPG, PNG, GIF, WebP
                  <br />
                  Tamaño máximo: 2MB
                </p>
              </div>

              {selectedFile && (
                <div className="text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                    <span className="text-xs text-slate-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowUploadLogoModal(false)}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              disabled={actionLoading}
            >
              Cancelar
            </button>
            <button
              onClick={handleUploadLogo}
              disabled={actionLoading || !selectedFile}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Subiendo...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Actualizar Foto</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Estados de carga y error (mantener igual)
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-2 text-slate-600">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => loadUsers()}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-yellow-800 font-semibold">Acceso restringido</h3>
          <p className="text-yellow-600">Solo los administradores pueden acceder a esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestión de Usuarios</h1>
          {/* <p className="text-slate-600">
            {stats ? `${stats.total_usuarios} usuarios registrados` : 'Cargando estadísticas...'}
          </p> */}
        </div>

      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
            <div className="text-sm text-slate-600">Total Usuarios</div>
            <div className="text-2xl font-bold text-slate-900">{stats.total_usuarios}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
            <div className="text-sm text-slate-600">Administradores</div>
            <div className="text-2xl font-bold text-red-600">{stats.total_admins}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
            <div className="text-sm text-slate-600">Usuarios</div>
            <div className="text-2xl font-bold text-blue-600">{stats.total_usuarios_reg}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
            <div className="text-sm text-slate-600">Nuevos Hoy</div>
            <div className="text-2xl font-bold text-emerald-600">{stats.nuevos_hoy}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
            <div className="text-sm text-slate-600">Empresas</div>
            <div className="text-2xl font-bold text-purple-600">{companies.length}</div>
          </div>
        </div>
      )}

      {/* Filtros y Búsqueda */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar usuarios, empresas, cargos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  loadUsers(1);
                }
              }}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            {/*             <select
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                loadUsers(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Todos los roles</option>
              {userService.getAvailableRoles().map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Todas las empresas</option>
              {companies.map((company, index) => (
                <option key={index} value={company}>
                  {truncateText(company, 30)}
                </option>
              ))}
            </select>
            <button
              onClick={() => loadUsers(1)}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Filter className="w-4 h-4" />
              <span>Aplicar filtros</span>
            </button> */}


            <div className="flex space-x-2 mt-4 sm:mt-0">
              <button
                onClick={() => loadUsers()}
                className="flex items-center space-x-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Actualizar</span>
              </button>
              <button
                onClick={handleCreateUser}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                <span>Nuevo Usuario</span>
              </button>
            </div>

          </div>

        </div>
      </div>

      {/* Tabla de Usuarios - ACTUALIZADA */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase tracking-wider">
                  Usuario / Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase tracking-wider">
                  Contacto / Cargo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase tracking-wider">
                  Último Acceso
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-900 uppercase tracking-wider w-24">
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <button
                            onClick={() => handleLogoClick(user)}
                            className="group relative focus:outline-none"
                            title="Haz clic para cambiar la foto de perfil"
                          >
                            {user.logo ? (
                              <>
                                <img
                                  src={user.logo}
                                  alt={user.company}
                                  className="w-10 h-10 rounded-full object-cover border border-slate-200 group-hover:opacity-80 transition-opacity"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/40x40?text=LOGO';
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Camera className="w-4 h-4 text-white" />
                                </div>
                              </>
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 group-hover:bg-slate-200 transition-colors">
                                <Building className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
                              </div>
                            )}
                          </button>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{user.name}</div>
                          <div className="text-xs text-slate-600 flex items-center">
                            <Building className="w-3 h-3 mr-1" />
                            {truncateText(user.company, 30)}
                          </div>
                          <div className="text-xs text-slate-500">Registro: {user.joinDate}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-slate-600">
                            <Mail className="w-4 h-4 mr-2" />
                            <span className="truncate max-w-[200px]">{user.email}</span>
                          </div>
                          <div className="flex items-center text-sm text-slate-600">
                            <Phone className="w-4 h-4 mr-2" />
                            {user.phone}
                          </div>
                        </div>
                        {user.position && user.position !== 'Sin posición' && (
                          <div className="mt-2">
                            <span className="inline-block px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded">
                              {truncateText(user.position, 25)}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.status)}`}>
                        {user.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        {user.lastLogin}
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex justify-center space-x-1">

                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Editar usuario"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        {/*                         <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                          title="Desactivar usuario"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button> */}

                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="text-slate-500">
                      {searchTerm || selectedCompany || selectedRole ?
                        'No se encontraron usuarios con los filtros aplicados' :
                        'No hay usuarios registrados'}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Información de resultados */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-600 mb-2 sm:mb-0">
              Mostrando {filteredUsers.length} de {users.length} usuarios
              {selectedCompany && ` • Empresa: ${selectedCompany}`}
              {selectedRole && ` • Rol: ${selectedRole}`}
            </div>
            <div className="text-sm text-slate-600">
              Total registros: {pagination.total}
            </div>
          </div>
        </div>

        {/* Paginación */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Página {pagination.page} de {pagination.pages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded border ${pagination.page === 1
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                >
                  Anterior
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className={`px-3 py-1 rounded border ${pagination.page === pagination.pages
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resumen de filtros activos */}
      {(searchTerm || selectedRole || selectedCompany) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-blue-800">
              <strong>Filtros activos:</strong>
              {searchTerm && ` • Búsqueda: "${searchTerm}"`}
              {selectedRole && ` • Rol: ${userService.getAvailableRoles().find(r => r.id === selectedRole)?.name || selectedRole}`}
              {selectedCompany && ` • Empresa: ${selectedCompany}`}
            </div>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedRole('');
                setSelectedCompany('');
                loadUsers(1);
              }}
              className="mt-2 sm:mt-0 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Limpiar todos los filtros
            </button>
          </div>
        </div>
      )}

      {/* Renderizar modales */}
      {showCreateModal && renderCreateModal()}
      {showEditModal && renderEditModal()}
      {showDeleteModal && renderDeleteModal()}
      {showUploadLogoModal && renderUploadLogoModal()}
    </div>
  );
};

export default Users;